import { useCallback, useMemo, useState } from "react";
import { OnChangeFn, RowSelectionState } from "@tanstack/react-table";
import { NestedEditorRow } from "./nestedRowTypes";
import {
    buildNestedSelectAllRowSelection,
    collectNestedGroupRowIds,
    collectSelectableNestedRowIds,
    countSelectedNestedRows,
    isNestedRowSelectable,
} from "./nestedSelection";
import { isRowPending } from "./nestedPendingAdds";

interface UseNestedEditorSelectionParams {
    nestedRows: NestedEditorRow[];
    // A row minted by an inline Add is inert until its name lands.
    pendingDraftIds: ReadonlySet<string>;
}

export interface NestedEditorSelection {
    rowSelection: RowSelectionState;
    handleRowSelectionChange: OnChangeFn<RowSelectionState>;
    enableRowSelection: (row: NestedEditorRow) => boolean;
    // Number of selectable rows currently in the tree, sizing "Select all".
    selectableCount: number;
    // Number of currently-selected selectable rows (accounts + groups), driving
    // the footer's "N selected" total.
    selectedVisibleCount: number;
    // Ids of the currently-selected custom group rows, for bulk Delete (groups
    // have no Enable/Archive, so they only participate in delete).
    selectedGroupIds: string[];
    selectAll: () => void;
    clearSelection: () => void;
}

// Owns the nested editor's checkbox selection: a controlled `rowSelection`, the
// selectable-row predicate (accounts + custom groups, never structural rows),
// and select-all / clear that operate over the live selectable row ids.
export const useNestedEditorSelection = ({
    nestedRows,
    pendingDraftIds,
}: UseNestedEditorSelectionParams): NestedEditorSelection => {
    const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

    const selectableRowIds = useMemo(
        () => collectSelectableNestedRowIds(nestedRows),
        [nestedRows],
    );

    const selectedVisibleCount = useMemo(
        () => countSelectedNestedRows({ selectableRowIds, rowSelection }),
        [selectableRowIds, rowSelection],
    );

    const selectedGroupIds = useMemo(
        () =>
            collectNestedGroupRowIds(nestedRows).filter(
                (id) => rowSelection[id],
            ),
        [nestedRows, rowSelection],
    );

    const enableRowSelection = useCallback(
        (row: NestedEditorRow) =>
            isNestedRowSelectable(row) && !isRowPending(row, pendingDraftIds),
        [pendingDraftIds],
    );

    const handleRowSelectionChange = useCallback<OnChangeFn<RowSelectionState>>(
        (updater) =>
            setRowSelection((prev) =>
                typeof updater === "function" ? updater(prev) : updater,
            ),
        [],
    );

    const selectAll = useCallback(
        () =>
            setRowSelection(buildNestedSelectAllRowSelection(selectableRowIds)),
        [selectableRowIds],
    );

    const clearSelection = useCallback(() => setRowSelection({}), []);

    return {
        rowSelection,
        handleRowSelectionChange,
        enableRowSelection,
        selectableCount: selectableRowIds.length,
        selectedVisibleCount,
        selectedGroupIds,
        selectAll,
        clearSelection,
    };
};
